import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_BASE } from "@/api/client";
import {
  useAbandonPlaythrough,
  useFollowProject,
  useFollows,
  useMyPlaythroughs,
  usePlays,
  useResumePlaythrough,
  useSession,
  useStartPlaythrough,
  useSubmitPlayStep,
  useUnfollowProject,
} from "@/api/queries";
import type { MyPlaythrough, PlaySummary, Playthrough } from "@/api/types";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { Modal } from "@/components/Modal";
import { ChoiceItem } from "@/components/ChoiceItem";
import { RemoteState } from "@/components/RemoteState";
import { useUiStore } from "@/store/ui";

/** 海报底色按项目 id 稳定映射到基线的四种色（纯视觉，不承载业务数据）。 */
const POSTER_TONES = ["poster-vermilion", "poster-indigo", "poster-jade", "poster-ochre"];

function posterTone(projectId: string): string {
  let hash = 0;
  for (const char of projectId) hash = (hash * 31 + char.charCodeAt(0)) % 997;
  return POSTER_TONES[hash % POSTER_TONES.length] ?? POSTER_TONES[0]!;
}

/**
 * 故事广场（原型 `renderDiscover`）。观众侧视图，隐藏侧栏与顶栏。
 *
 * **`mixed`：品牌文案静态，业务数据全部真实**（RULES §11.3）——
 *
 * - **静态**：顶部品牌文案与「共创社区」引导；
 * - **远端**：作品卡片来自**公开作品目录** `GET /plays`（只列已发布作品，`specs/15` §12），
 *   每张卡对应一个真实 `project_id`，点开即播放**该项目自己的** `storylet`（M4 三端点）；
 *   「继续上次旅程」来自**本人**游玩列表 `GET /audience/playthroughs`（M8 · PR-1518），
 *   点击用 `GET /audience/playthroughs/{id}` 恢复当前幕；
 *   「追更」来自**本人**订阅 `GET /audience/follows`（跨设备，`ADR-024`）。
 *
 * 此前的静态故事卡片与「选择画像」是**无数据来源的展示数据**，已移除
 * （CONSTITUTION D1/D2 · PR-1517）。
 *
 * @dataSource mixed
 */
export function DiscoverView() {
  const navigate = useNavigate();
  const discoverGenre = useUiStore((state) => state.discoverGenre);
  const setDiscoverGenre = useUiStore((state) => state.setDiscoverGenre);
  const pushToast = useUiStore((state) => state.pushToast);

  const [active, setActive] = useState<{
    project_id: string;
    name: string;
    genre: string | null;
  } | null>(null);
  const [play, setPlay] = useState<Playthrough | null>(null);
  const plays = usePlays();
  const session = useSession();
  // 该端点需登录：未登录不发请求（否则只会拿到 401）。
  const mine = useMyPlaythroughs(Boolean(session.data));
  // 追更订阅（跨设备，`ADR-024`）：本人列表来自后端；未登录不发请求。
  const follows = useFollows(Boolean(session.data));
  const follow = useFollowProject();
  const unfollow = useUnfollowProject();
  const followedIds = new Set((follows.data?.items ?? []).map((item) => item.project_id));
  const start = useStartPlaythrough();
  const resume = useResumePlaythrough();
  const step = useSubmitPlayStep();
  const abandon = useAbandonPlaythrough();

  const items = plays.data ?? [];
  const genres: string[] = [
    "全部",
    ...Array.from(
      new Set(items.map((item) => item.genre).filter((genre): genre is string => Boolean(genre))),
    ),
  ];
  const visiblePlays =
    discoverGenre === "全部" ? items : items.filter((item) => item.genre === discoverGenre);

  /**
   * 调和题材筛选与**真实**目录（`discoverGenre` 是持久化的界面状态）。
   *
   * 目录改由 `GET /plays` 驱动后，旧版本存下的静态中文题材（"悬疑" / "女性成长" / "志怪"）
   * 在真实题材集合里已不存在——不调和的话，广场会**停在一个永远为空的筛选**上
   * （用户只看得到"这个分类还在准备中"，且没有任何一个题材胶囊是选中态）。
   * 与 `ProjectSwitcher` 的幽灵 ID 调和同一类：**持久化的界面状态必须与真实数据对上**。
   */
  useEffect(() => {
    if (discoverGenre === "全部" || items.length === 0) return;
    if (genres.includes(discoverGenre)) return;
    setDiscoverGenre("全部");
  }, [discoverGenre, genres, items.length, setDiscoverGenre]);

  const latest = mine.data?.items[0] ?? null;
  const busy = start.isPending || step.isPending || resume.isPending || abandon.isPending;
  const failure = start.error ?? step.error ?? resume.error ?? abandon.error;
  const errorText = failure instanceof Error ? failure.message : "";

  function openPlay(item: PlaySummary) {
    setActive({ project_id: item.project_id, name: item.name, genre: item.genre });
    setPlay(null);
    start.reset();
    resume.reset();
    step.reset();
  }

  function closePlay() {
    setActive(null);
    setPlay(null);
  }

  function beginPlay() {
    if (!active) return;
    start.mutate(active.project_id, {
      onSuccess: (view) => {
        setPlay(view);
        pushToast({
          title: "已进入故事",
          detail: "你的每次选择都会被真实记录，成为这个故事的观众信号",
          mark: "✓",
        });
      },
    });
  }

  /** 续玩：恢复本人进行中的游玩（`GET /audience/playthroughs/{id}`）。 */
  function resumeLatest(item: MyPlaythrough) {
    resume.mutate(item.id, {
      onSuccess: (view) => {
        const summary = items.find((entry) => entry.project_id === item.project_id);
        setActive({
          project_id: item.project_id,
          name: item.project_name ?? summary?.name ?? item.project_id,
          genre: summary?.genre ?? null,
        });
        setPlay(view);
      },
    });
  }

  function submitStep(chosenKey?: string) {
    if (!play) return;
    step.mutate(
      { playthroughId: play.id, chosenKey },
      {
        onSuccess: (view) => {
          setPlay(view);
          if (view.status === "FINISHED") {
            pushToast({
              title: "这段旅程结束了",
              detail: "你的选择已成为观众信号，不会直接改写正式剧本",
              mark: "✓",
            });
          }
        },
      },
    );
  }

  /**
   * 主动放弃这段旅程（`specs/15` §3 · §10 第 10 条）。
   *
   * ⚠️ 放弃**不丢已走的步数**——服务端只标记状态，`play_steps` 与埋点全部保留
   * （"这个人玩过"这个事实连同其分析价值必须留存）。
   */
  function abandonPlay() {
    if (!play) return;
    abandon.mutate(play.id, {
      onSuccess: (view) => {
        setPlay(view);
        pushToast({
          title: "已放下这段旅程",
          detail: "你走过的每一步都还在，只是不再继续",
          mark: "✓",
        });
      },
    });
  }

  return (
    <div className="platform-scroll discover-view">
      <section className="discover-heading">
        <div>
          <span className="eyebrow">故事广场 · 观众侧</span>
          <h1>
            今晚，你想把哪个故事
            <br />
            讲成自己的版本？
          </h1>
          <p>
            选择不是答题。你做过的决定、放弃的路径和留下的脑洞，都会构成一份只属于你的故事旅程。
          </p>
        </div>
      </section>

      {mine.isError ? (
        <p className="form-error" role="alert">
          继续游玩加载失败：
          {mine.error instanceof Error ? mine.error.message : "请稍后重试"}
        </p>
      ) : null}

      {latest ? (
        <section className="continue-story" aria-label="继续游玩">
          <div className="continue-cover">
            <span>{(latest.project_name ?? "作").slice(0, 1)}</span>
            <small>{latest.project_name ?? "作品"}</small>
          </div>
          <div className="continue-copy">
            <span className="eyebrow">继续上次旅程 · 已走 {latest.steps_taken} 步</span>
            <h2>{latest.current_storylet_title ?? "从上次离开的那一幕继续"}</h2>
            <p>继续这一段旅程——你做过的每个选择都已真实记录。</p>
          </div>
          <button
            className="button button-accent"
            type="button"
            disabled={resume.isPending}
            onClick={() => resumeLatest(latest)}
          >
            {resume.isPending ? "载入中…" : "继续这次游玩"}
          </button>
        </section>
      ) : null}

      <RemoteState queries={[plays]} title="作品目录加载失败">
        <section className="story-shelf" aria-labelledby="story-shelf-title">
          <header className="shelf-heading">
            <div>
              <span className="eyebrow">可玩作品</span>
              <h2 id="story-shelf-title">编剧完成、已经上架的故事</h2>
            </div>
            {genres.length > 1 ? (
              <div className="genre-pills">
                {genres.map((genre) => (
                  <button
                    key={genre}
                    type="button"
                    className={discoverGenre === genre ? "is-active" : ""}
                    onClick={() => setDiscoverGenre(genre)}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            ) : null}
          </header>
          <div className="story-card-grid">
            {visiblePlays.length === 0 ? (
              <div className="story-filter-empty">
                <EmptyState
                  title={items.length === 0 ? "还没有已上架的作品" : "这个分类还在准备中"}
                  description={
                    items.length === 0
                      ? "编剧在创作台完成一部作品并发布后，它就会出现在这里。"
                      : "先看看其他作品，新的互动项目会在发布后进入广场。"
                  }
                />
              </div>
            ) : (
              visiblePlays.map((item) => (
                <PlayCard
                  key={item.project_id}
                  item={item}
                  followed={followedIds.has(item.project_id)}
                  onPlay={() => openPlay(item)}
                  onFollow={() => {
                    if (!session.data) {
                      // 追更是账号数据（换设备也在）——未登录如实引导，不自作主张写本机状态。
                      pushToast({
                        title: "登录后才能追更",
                        detail: "追更会跟着你的账号走，换设备也在",
                        mark: "!",
                      });
                      navigate("/login");
                      return;
                    }
                    const isFollowed = followedIds.has(item.project_id);
                    if (isFollowed) {
                      unfollow.mutate(item.project_id);
                    } else {
                      follow.mutate(item.project_id);
                    }
                    pushToast({
                      title: isFollowed ? "已取消追更" : "已加入追更",
                      detail: item.name,
                      mark: "✓",
                    });
                  }}
                />
              ))
            )}
          </div>
        </section>
      </RemoteState>

      <section className="audience-idea-band">
        <div>
          <span className="eyebrow">一句话也能参与</span>
          <h2>不必会写分支，先告诉创作者你还想看见什么</h2>
          <p>
            普通观众提供脑洞与偏好信号；半专业创作者把信号整理成候选；专业编剧决定什么进入正式作品。
          </p>
        </div>
        <Button variant="primary" onClick={() => navigate("/community")}>
          去共创社区
        </Button>
      </section>

      <Modal
        open={Boolean(active)}
        onClose={closePlay}
        eyebrow={active ? (active.genre ?? "续玩") : ""}
        title={active ? active.name : ""}
        footer={
          <PlayFooter
            play={play}
            busy={busy}
            canStart={Boolean(active)}
            onStart={beginPlay}
            onContinue={() => submitStep()}
            onClose={closePlay}
          />
        }
      >
        <PlayBody
          play={play}
          errorText={errorText}
          busy={busy}
          onChoose={submitStep}
          onAbandon={abandonPlay}
        />
      </Modal>
    </div>
  );
}

function PlayCard({
  item,
  followed,
  onPlay,
  onFollow,
}: {
  item: PlaySummary;
  followed: boolean;
  onPlay: () => void;
  onFollow: () => void;
}) {
  return (
    <article className="audience-story-card">
      <div className={`story-poster ${posterTone(item.project_id)}`}>
        {/* 作品封面（`ADR-023`）：有值才渲染；无封面保持原型渐变海报（D2：不回落静态图）。 */}
        {item.cover_url ? (
          <img className="story-poster-image" src={`${API_BASE}${item.cover_url}`} alt="" />
        ) : null}
        <span>{item.genre ?? "未分类"}</span>
        <strong>{item.name}</strong>
        <small>
          {item.playthrough_count > 0
            ? `${item.playthrough_count} 人走进过这个故事`
            : "还没有观众走进"}
        </small>
      </div>
      <div className="story-card-copy">
        <div className="story-card-meta">
          <span>{item.genre ?? "未分类"}</span>
          <span>{item.storylet_count} 个分支单元</span>
          {/* 追更订阅数（现算·`ADR-024` 复核条件 2）：**0 时整块不渲染**——
              "0 人追更"是一句没有信息量的话，占位只会让卡片显得更空。
              与 `synopsis` / `cover_url` 的"有值才渲染"同一姿态（D2）。 */}
          {item.follower_count > 0 ? <span>{item.follower_count} 人追更</span> : null}
        </div>
        {/* 发布者自撰的一句话简介（`ADR-021`）；为空则整行不渲染（D2）。 */}
        {item.synopsis ? <p>{item.synopsis}</p> : null}
        <div className="story-card-actions">
          <button className="button button-primary" type="button" onClick={onPlay}>
            开始
          </button>
          <button className="button button-quiet" type="button" onClick={onFollow}>
            {followed ? "已追更" : "追更"}
          </button>
        </div>
      </div>
    </article>
  );
}

/** 播放器正文：只呈现（选择由 `onChoose` 上报），不自己发请求。 */
function PlayBody({
  play,
  errorText,
  busy,
  onChoose,
  onAbandon,
}: {
  play: Playthrough | null;
  errorText: string;
  busy: boolean;
  onChoose: (chosenKey?: string) => void;
  onAbandon: () => void;
}) {
  if (errorText) {
    return (
      <p className="form-error" role="alert">
        {errorText}
      </p>
    );
  }

  if (!play) {
    return (
      <div className="section-title">
        <div>
          <h3>准备好了吗？</h3>
          <p>开始后，你会从故事的第一幕进入，每一步选择都会被真实记录。</p>
        </div>
      </div>
    );
  }

  if (play.status === "FINISHED" || !play.storylet) {
    return (
      <div className="section-title">
        <div>
          <h3>这段旅程结束了</h3>
          <p>你走过了 {play.steps_taken} 步。你的选择已成为观众信号，不会直接改写正式剧本。</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <span className="eyebrow">{play.storylet.dramatic_function}</span>
      <div className="section-title">
        <div>
          <h3>{play.storylet.title}</h3>
          <p>{play.storylet.scene_task}</p>
        </div>
      </div>
      {play.choices.length > 0 ? (
        <>
          <div className="section-title">
            <div>
              <h3>现在，你会怎么做？</h3>
              <p>每个选项都会改变状态与后续成本，没有“正确答案”</p>
            </div>
          </div>
          <div className="choice-list">
            {play.choices.map((choice, index) => (
              <ChoiceItem
                key={choice.key}
                index={index}
                label={choice.action}
                effect={`${choice.immediate_gain} ｜ ${choice.immediate_cost}`}
                disabled={busy}
                onClick={() => onChoose(choice.key)}
              />
            ))}
          </div>
        </>
      ) : (
        <p style={{ color: "var(--ink-soft)", fontSize: "12px", lineHeight: 1.8 }}>
          这一幕没有需要你决定的分支——继续，走向下一幕。
        </p>
      )}
      {/* 主动放弃（`specs/15` §3 · §10 第 10 条）：`ABANDONED` 的**唯一**入口。
          措辞如实说明"不丢记录"——否则玩家会以为放弃等于删除。
          ⚠️ 只在进行中呈现：已结束态由上面的分支接管，不再给第二次放弃。 */}
      {play.status === "IN_PROGRESS" ? (
        <div className="story-card-actions">
          <button className="button button-quiet" type="button" onClick={onAbandon} disabled={busy}>
            放下这段旅程
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** 播放器页脚按钮组：随游玩状态切换（纯受控，不自己发请求）。 */
function PlayFooter({
  play,
  busy,
  canStart,
  onStart,
  onContinue,
  onClose,
}: {
  play: Playthrough | null;
  busy: boolean;
  canStart: boolean;
  onStart: () => void;
  onContinue: () => void;
  onClose: () => void;
}) {
  if (!play) {
    return (
      <>
        <Button variant="quiet" onClick={onClose}>
          稍后再玩
        </Button>
        <Button variant="primary" onClick={onStart} disabled={busy || !canStart}>
          {busy ? "进入中…" : "开始这次游玩"}
        </Button>
      </>
    );
  }

  if (play.status === "FINISHED" || !play.storylet) {
    return (
      <Button variant="primary" onClick={onClose}>
        完成
      </Button>
    );
  }

  if (play.choices.length > 0) {
    return (
      <Button variant="quiet" onClick={onClose}>
        先离开（进度已保存）
      </Button>
    );
  }

  return (
    <>
      <Button variant="quiet" onClick={onClose}>
        稍后再玩
      </Button>
      <Button variant="primary" onClick={onContinue} disabled={busy}>
        {busy ? "推进中…" : "继续"}
      </Button>
    </>
  );
}
